import { Box, Grid, Typography } from "@mui/material";
import React from "react";
import "./History.css";
import { Images } from "../../helpers/Images";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";

const HIGHLIGHTS = [
  {
    title: "SDG-Aligned Themes",
    description:
      "Each season focuses on a United Nations Sustainable Development Goal.",
  },
  {
    title: "Yearly Innovation",
    description:
      "New challenges that push robotics, AI, and creative problem-solving.",
  },
  {
    title: "Global Impact",
    description:
      "Students learn to address real-world challenges through technology.",
  },
];

const HISTORY_DATA = [
  {
    year: 2025,
    title: "AI Farm",
    sdg: "SDG 12: Responsible Consumption and Production",
    description:
      "This year's theme emphasizes the integration of AI and robotics in agriculture to achieve sustainable and efficient food production. Participants are challenged to design smart farming systems that reduce resource waste, optimize crop yields, and promote eco-friendly practices.",
    sdgImage: Images.sdg12,
    matImage: Images.mat2025,
  },
  {
    year: 2024,
    title: "Doctor Robot",
    sdg: "SDG 3: Good Health and Well-being",
    description:
      "In 2024, the challenge spotlighted healthcare innovations through robotics and AI. Participants developed intelligent medical assistant systems aimed at enhancing diagnostics, supporting patient care, and automating health services.",
    sdgImage: Images.sdg3,
    matImage: Images.mat2024,
  },
  {
    year: 2023,
    title: "Food Security",
    sdg: "SDG 2: Zero Hunger",
    description:
      "This year focused on combating hunger and promoting sustainable agriculture. Participants used robotics and artificial intelligence to propose innovative farming techniques, improve food distribution systems, and support communities in need.",
    sdgImage: Images.sdg2,
    matImage: Images.mat2023,
  },
  {
    year: 2022,
    title: "Climate Change",
    sdg: "SDG 13: Climate Action",
    description:
      "The 2022 theme inspired participants to develop robotic solutions that address the urgent issue of climate change. Competitors worked on projects aimed at reducing emissions, improving energy efficiency, and raising environmental awareness.",
    sdgImage: Images.sdg13,
    matImage: Images.mat2022,
  },
  {
    year: "2020–2021",
    title: "Save the Seas",
    sdg: "SDG 14: Life Below Water",
    description:
      "In these two years, the competition centered on preserving marine ecosystems and reducing ocean pollution. Participants designed robotic solutions to monitor marine environments, remove waste from oceans, and protect aquatic life.",
    sdgImage: Images.sdg14,
    matImage: Images.mat2021,
  },
];

const HistoryTemplate = () => {
  const scrollToThemes = () => {
    document
      .getElementById("history-themes")
      .scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box className="historyPage">
      <Box
        className="historyHero"
        sx={{
          backgroundImage: `radial-gradient(1100px 620px at 15% 15%, rgba(35, 174, 244, 0.38), transparent 58%),
            radial-gradient(900px 520px at 88% 25%, rgba(193, 26, 131, 0.32), transparent 55%),
            linear-gradient(180deg, rgba(10, 14, 24, 0.78) 0%, rgba(10, 14, 24, 0.62) 100%),
            url(${Images.website})`,
        }}
      >
        <Box
          className="historyHeroInner"
          sx={{
            padding: {
              xs: "56px 20px 40px",
              sm: "72px 60px 48px",
              lg: "88px 120px 56px",
            },
          }}
        >
          <Typography className="historyHeroEyebrow">We Do Challenge</Typography>
          <Typography className="historyHeroTitle">Our History</Typography>
          <Box className="historyHeroAccent" />
          <Typography className="historyHeroSubtitle">
            The We Do Challenge aligns with Sustainable Development Goals by
            encouraging young minds to engage in robotics, programming, and
            innovation. Each year, a new theme helps participants address global
            challenges through technology.
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={scrollToThemes}
            className="historyHeroCta"
          >
            Explore Themes
          </Box>

          <Box className="historyHeroHighlights">
            {HIGHLIGHTS.map((item) => (
              <Box key={item.title} className="historyHeroHighlight">
                <Typography className="historyHeroHighlightTitle">
                  {item.title}
                </Typography>
                <Typography className="historyHeroHighlightText">
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        id="history-themes"
        sx={{
          padding: { xs: "40px 20px", sm: "50px 60px", lg: "60px 120px 80px" },
          maxWidth: "1500px",
          margin: "0 auto",
        }}
      >
        <Box className="historySectionHeader">
          <Typography className="historySectionTitle">
            Competition Themes
          </Typography>
          <Box className="historySectionAccent" />
          <Typography className="historySectionSubtitle">
            A look back at how We Do Challenge has evolved — from ocean
            conservation to AI-powered agriculture.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {HISTORY_DATA.map((item, index) => (
            <Grid item xs={12} lg={6} key={`${item.year}-${item.title}`}>
              <Box className="historyCard">
                <Box className="historyCardTop">
                  <Box className="historyCardMeta">
                    <Typography className="historyCardYear">
                      {item.year}
                    </Typography>
                    <Typography className="historyCardTitle">
                      {item.title}
                    </Typography>
                    <Box className="historyCardSdg">
                      <PublicOutlinedIcon sx={{ fontSize: 16 }} />
                      <span>{item.sdg}</span>
                    </Box>
                  </Box>
                  <Box className="historyCardSdgImageWrap">
                    <Box
                      component="img"
                      src={item.sdgImage}
                      alt={`SDG for ${item.title}`}
                      className="historyCardSdgImage"
                    />
                  </Box>
                </Box>

                <Typography className="historyCardDescription">
                  {item.description}
                </Typography>

                <Box className="historyCardMatWrap">
                  {item.matImage ? (
                    <Box
                      component="img"
                      src={item.matImage}
                      alt={`Competition mat for ${item.title}`}
                      className="historyCardMat"
                    />
                  ) : (
                    <Box className="historyCardComingSoon">
                      <EmojiEventsOutlinedIcon />
                      <Typography>Coming Soon</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default HistoryTemplate;
